import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Baby, Heart, MessageCircle, Users, Eye, Trash2, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const FormsManager = () => {
  const [activeTab, setActiveTab] = useState('baptism');
  const [baptismRequests, setBaptismRequests] = useState([]);
  const [weddingRequests, setWeddingRequests] = useState([]);
  const [counselingRequests, setCounselingRequests] = useState([]);
  const [membershipRequests, setMembershipRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const [baptism, wedding, counseling, membership] = await Promise.all([
        api.get('/admin/forms/baptism'),
        api.get('/admin/forms/wedding'),
        api.get('/admin/forms/counseling'),
        api.get('/admin/forms/membership')
      ]);
      
      setBaptismRequests(baptism.data);
      setWeddingRequests(wedding.data);
      setCounselingRequests(counseling.data);
      setMembershipRequests(membership.data);
    } catch (error) {
      toast.error('Failed to load form submissions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, requestId, status) => {
    try {
      await api.put(`/admin/forms/${type}/${requestId}/status?status=${status}`);
      toast.success('Status updated successfully');
      fetchAllRequests();
      setSelectedRequest(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteRequest = async (type, requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await api.delete(`/admin/forms/${type}/${requestId}`);
      toast.success('Request deleted');
      fetchAllRequests();
      setSelectedRequest(null);
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Approved' },
      completed: { icon: CheckCircle, color: 'bg-blue-100 text-blue-800', label: 'Completed' },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const renderBaptismDetails = (request) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Child's Name</label>
          <p className="text-gray-900 dark:text-white">{request.child_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Date of Birth</label>
          <p className="text-gray-900 dark:text-white">{request.date_of_birth}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Gender</label>
          <p className="text-gray-900 dark:text-white capitalize">{request.gender}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Preferred Date</label>
          <p className="text-gray-900 dark:text-white">{request.preferred_date}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Father's Name</label>
          <p className="text-gray-900 dark:text-white">{request.father_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Mother's Name</label>
          <p className="text-gray-900 dark:text-white">{request.mother_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Contact Email</label>
          <p className="text-gray-900 dark:text-white">{request.contact_email}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Contact Phone</label>
          <p className="text-gray-900 dark:text-white">{request.contact_phone}</p>
        </div>
      </div>
      {request.additional_notes && (
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Additional Notes</label>
          <p className="text-gray-900 dark:text-white mt-1">{request.additional_notes}</p>
        </div>
      )}
    </div>
  );

  const renderWeddingDetails = (request) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Groom's Name</label>
          <p className="text-gray-900 dark:text-white">{request.groom_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Bride's Name</label>
          <p className="text-gray-900 dark:text-white">{request.bride_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Contact Email</label>
          <p className="text-gray-900 dark:text-white">{request.contact_email}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Contact Phone</label>
          <p className="text-gray-900 dark:text-white">{request.contact_phone}</p>
        </div>
        <div className="col-span-2">
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Preferred Wedding Date</label>
          <p className="text-gray-900 dark:text-white">{request.preferred_date}</p>
        </div>
      </div>
      {request.additional_notes && (
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Additional Notes</label>
          <p className="text-gray-900 dark:text-white mt-1">{request.additional_notes}</p>
        </div>
      )}
    </div>
  );

  const renderCounselingDetails = (request) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Full Name</label>
          <p className="text-gray-900 dark:text-white">{request.full_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Counseling Type</label>
          <p className="text-gray-900 dark:text-white capitalize">{request.counseling_type}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</label>
          <p className="text-gray-900 dark:text-white">{request.email}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone</label>
          <p className="text-gray-900 dark:text-white">{request.phone}</p>
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Message</label>
        <p className="text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{request.message}</p>
      </div>
    </div>
  );

  const renderMembershipDetails = (request) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Full Name</label>
          <p className="text-gray-900 dark:text-white">{request.full_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</label>
          <p className="text-gray-900 dark:text-white">{request.email}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone</label>
          <p className="text-gray-900 dark:text-white">{request.phone}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Address</label>
          <p className="text-gray-900 dark:text-white">{request.address}</p>
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Why Join Our Church</label>
        <p className="text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{request.reason}</p>
      </div>
    </div>
  );

  const tabs = [
    { id: 'baptism', label: 'Baptism Requests', icon: Baby, count: baptismRequests.length, color: 'text-blue-600' },
    { id: 'wedding', label: 'Wedding Requests', icon: Heart, count: weddingRequests.length, color: 'text-pink-600' },
    { id: 'counseling', label: 'Counseling Requests', icon: MessageCircle, count: counselingRequests.length, color: 'text-green-600' },
    { id: 'membership', label: 'Membership Requests', icon: Users, count: membershipRequests.length, color: 'text-purple-600' }
  ];

  const getCurrentRequests = () => {
    switch (activeTab) {
      case 'baptism': return baptismRequests;
      case 'wedding': return weddingRequests;
      case 'counseling': return counselingRequests;
      case 'membership': return membershipRequests;
      default: return [];
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Form Submissions</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage baptism, wedding, counseling, and membership requests
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedRequest(null); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 shadow-sm'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className={`w-5 h-5 ${activeTab === tab.id ? tab.color : 'text-gray-500'}`} />
              <span className={`font-medium ${activeTab === tab.id ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                {tab.label}
              </span>
              {tab.count > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
      ) : selectedRequest ? (
        <Card className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Request Details</h3>
              <div className="flex items-center space-x-3">
                {getStatusBadge(selectedRequest.status)}
                <span className="text-sm text-gray-500">
                  Submitted: {new Date(selectedRequest.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <Button onClick={() => setSelectedRequest(null)} variant="outline" size="sm">
              Back to List
            </Button>
          </div>

          {activeTab === 'baptism' && renderBaptismDetails(selectedRequest)}
          {activeTab === 'wedding' && renderWeddingDetails(selectedRequest)}
          {activeTab === 'counseling' && renderCounselingDetails(selectedRequest)}
          {activeTab === 'membership' && renderMembershipDetails(selectedRequest)}

          <div className="flex space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <select
              className="p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
              value={selectedRequest.status}
              onChange={(e) => updateStatus(activeTab, selectedRequest.id, e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
            <Button
              onClick={() => deleteRequest(activeTab, selectedRequest.id)}
              className="bg-red-600 hover:bg-red-700 ml-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Request
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {getCurrentRequests().length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">No requests found</p>
            </Card>
          ) : (
            getCurrentRequests().map((request) => (
              <Card key={request.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        {activeTab === 'baptism' && request.child_name}
                        {activeTab === 'wedding' && `${request.groom_name} & ${request.bride_name}`}
                        {activeTab === 'counseling' && request.full_name}
                      </h4>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <p>
                        {activeTab === 'baptism' && `Parents: ${request.father_name} & ${request.mother_name}`}
                        {activeTab === 'wedding' && `Date: ${request.preferred_date}`}
                        {activeTab === 'counseling' && `Type: ${request.counseling_type}`}
                      </p>
                      <p>Contact: {request.contact_email || request.email}</p>
                      <p>Submitted: {new Date(request.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Button onClick={() => setSelectedRequest(request)} size="sm" variant="outline">
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FormsManager;
